import { CopilotChat, type InputProps, type MessagesProps } from '@copilotkit/react-ui'
import React, { type MutableRefObject, useCallback, useEffect, useMemo, useRef, type FC, useState } from 'react'
import cx from 'classnames'
import { twMerge } from 'tailwind-merge'
import { type DefaultProps } from '@egw/components'
import { WEALTH_ROUTES } from '@egw/constants'
import { useLocation } from 'react-router'
import { useAiChatAction, threadId } from '@egw/ai-chat'
import { useCoAgentStateRender, useCopilotChat } from '@copilotkit/react-core'
import { ChatInput } from '../chat-input/chat-input'
import { Messages } from '../messages/messages'
import '@copilotkit/react-ui/styles.css'
import { ChatVariant } from '../../types/chat'
import { ChatLogo } from '../chat-logo/chat-logo'
import { showStockTimeseriesAction } from '../../actions/show-stock-timeseries'
import { DataVisualization } from '../data-visualization/data-visualization'

interface ChatProps extends DefaultProps {
  instructions?: string
  labels?: { title: string; initial: string }
  isInitialOpen?: boolean
  isShowBackButton?: boolean
  isChatOpen?: boolean
  setIsChatOpen?: (_isChatOpen: boolean) => void
}

// Wrapper components defined outside to avoid re-creation on every render
const createChatInputWrapper = (
  onOpenChat: () => void,
  isOverviewPage: boolean,
  isChatOpen: boolean,
  isEventSentRef: MutableRefObject<boolean>,
  setError: (error: string | Error | null) => void,
): FC<InputProps> => {
  const ChatInputWrapper: FC<InputProps> = (props) => (
    <ChatInput
      {...props}
      onOpenChat={onOpenChat}
      variant={isOverviewPage || isChatOpen ? ChatVariant.EXPANDED : ChatVariant.DEFAULT}
      isChatOpen={isChatOpen}
      isEventSentRef={isEventSentRef}
      setError={setError}
    />
  )
  ChatInputWrapper.displayName = 'ChatInputWrapper'
  return ChatInputWrapper
}

const createMessagesWrapper = (
  setIsChatOpen: (_open: boolean) => void,
  isShowBackButton: boolean,
  error: string | Error | null,
): FC<Omit<MessagesProps, 'RenderMessage'>> => {
  const CustomMessagesWrapper: FC<Omit<MessagesProps, 'RenderMessage'>> = (props) => (
    <Messages {...props} setIsChatOpen={setIsChatOpen} isShowBackButton={isShowBackButton} error={error} />
  )
  CustomMessagesWrapper.displayName = 'CustomMessagesWrapper'
  return CustomMessagesWrapper
}

// Visualization component defined outside to avoid re-creation on every render
const VisualizationRenderer: FC<{ children?: React.ReactNode } & Record<string, unknown>> = (props) => {
  const { file, children } = props as { file: string; children?: React.ReactNode }
  return (
    <>
      <DataVisualization threadId={threadId} key={file} fileId={file} />
      {children}
    </>
  )
}
VisualizationRenderer.displayName = 'VisualizationRenderer'

// Type for markdown tag renderers - matches CopilotKit's ComponentsMap type
type MarkdownTagRenderers = Record<string, FC<{ children?: React.ReactNode } & Record<string, unknown>>>

interface AgentState {
  current_step: string
}

// Inner component that uses CopilotKit hooks
const ChatContent: FC<ChatProps> = ({
  instructions,
  labels,
  className,
  testId,
  id,
  ariaLabel,
  isInitialOpen = false,
  isShowBackButton = true,
  isChatOpen,
  setIsChatOpen,
}) => {
  const { pathname } = useLocation()
  const { isLoading, reset } = useCopilotChat()
  const [error, setError] = useState<string | Error | null>(null)
  const pageRef = useRef<HTMLDivElement>(null)

  const baseClass = cx('flex flex-col flex-1 !bg-transparent mx-auto w-full', {
    'flex flex-col h-full': isChatOpen,
    'md:max-w-[816px]': !isChatOpen,
  })
  // Final class names
  const finalClasses = twMerge(baseClass, className)
  const isEventSentRef = useRef(false)

  const onOpenChat = (): void => {
    setIsChatOpen?.(true)
  }

  useEffect(() => {
    setIsChatOpen?.(isInitialOpen)
  }, [isInitialOpen, setIsChatOpen])

  // Reset chat when leaving page
  useEffect(() => {
    return () => {
      reset()
    }
  }, [pathname, reset])

  // Safari mobile keyboard fix
  useEffect(() => {
    if (!isChatOpen) return

    const handleScrollToTop = (): void => {
      window.scrollTo(0, 0)
    }

    // Check for visual viewport to handle resizing
    const visualViewport = window.visualViewport
    let offset = 0

    if (visualViewport) {
      let viewportWidth = window.innerWidth
      let viewportHeight = window.innerHeight

      const handleViewportResize = (event: Event): void => {
        const target = event.target as VisualViewport
        const page = pageRef.current

        if (!page) return

        if (viewportWidth !== target.width) {
          viewportWidth = window.innerWidth
          viewportHeight = window.innerHeight
        }

        if (viewportHeight - target.height > 150) {
          handleScrollToTop()
          const adjustment = viewportHeight - target.height - offset
          page.style.bottom = `${adjustment.toString()}px`
        } else if (viewportHeight === target.height || viewportHeight - target.height <= 150) {
          offset = viewportHeight - target.height
          page.style.bottom = '0px'
        }
      }

      visualViewport.addEventListener('resize', handleViewportResize)

      return (): void => {
        visualViewport.removeEventListener('resize', handleViewportResize)
        document.removeEventListener('touchend', handleScrollToTop)
      }
    }

    document.addEventListener('touchend', handleScrollToTop)

    return (): void => {
      document.removeEventListener('touchend', handleScrollToTop)
    }
  }, [isChatOpen])

  const isOverviewPage = pathname === WEALTH_ROUTES.OVERVIEW.path

  const ChatInputWrapper = createChatInputWrapper(
    onOpenChat,
    isOverviewPage,
    isChatOpen ?? false,
    isEventSentRef,
    setError,
  )
  const CustomMessagesWrapper = useMemo(
    () => createMessagesWrapper(setIsChatOpen ?? (() => undefined), isShowBackButton, error),
    [setIsChatOpen, isShowBackButton, error],
  )

  // TODO: remove this after testing
  const userChangedAgent = localStorage.getItem('selected-agent') ?? 'advice'

  const isLoadingRef = useRef(isLoading)
  isLoadingRef.current = isLoading

  const renderThinking = useCallback(({ state }: { state: AgentState }) => {
    if (!state.current_step || !isLoadingRef.current) {
      return null
    }

    return (
      <div className="flex items-start gap-2 my-4">
        <ChatLogo data-testid="chat-thinking-logo" />
        <span className="bg-gradient-to-r from-header via-[#adadad] to-header bg-clip-text text-transparent bg-[length:200%_100%] animate-gradient-shift">
          {state.current_step}
        </span>
      </div>
    )
  }, [])

  useCoAgentStateRender<AgentState>({
    name: userChangedAgent,
    render: renderThinking,
  })

  useAiChatAction(showStockTimeseriesAction)

  const customMarkdownTagRenderers = useMemo<MarkdownTagRenderers>(() => {
    return {
      visualization: VisualizationRenderer,
    }
  }, [])

  return (
    <div
      ref={pageRef}
      {...(testId ? { 'data-testid': testId } : {})}
      {...(id ? { id } : {})}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
      className={cx({
        'absolute bottom-0 left-0 right-0 top-0 flex flex-col z-10 bg-white overflow-y-auto': isChatOpen,
        'sticky bottom-0': !isChatOpen,
      })}
    >
      <CopilotChat
        instructions={instructions}
        labels={labels}
        className={finalClasses}
        onSubmitMessage={() => {
          setIsChatOpen?.(true)
        }}
        Input={ChatInputWrapper}
        Messages={isChatOpen ? CustomMessagesWrapper : () => null}
        onError={(errorObj) => {
          setError(errorObj.error ? (errorObj.error as string | Error) : 'An error occurred')
        }}
        data-testid="chat"
        markdownTagRenderers={customMarkdownTagRenderers}
      />
    </div>
  )
}

/**
 * Error boundary for Chat component.
 * Renders nothing if CopilotKit hooks fail (e.g., when provider errored).
 */
class ChatErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true }
  }

  componentDidCatch(error: Error): void {
    // Log but don't crash - chat will simply not render
    console.warn('[Chat] Error caught, hiding chat component:', error.message) // eslint-disable-line no-console
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return null // Don't render chat if CopilotKit is unavailable
    }
    return this.props.children
  }
}

/**
 * Chat component with built-in error boundary.
 * Gracefully handles CopilotKit unavailability by rendering nothing.
 */
export const Chat: FC<ChatProps> = (props) => {
  return (
    <ChatErrorBoundary>
      <ChatContent {...props} />
    </ChatErrorBoundary>
  )
}
